import {
  Board,
  BoardRevisions,
  BoardRevisionState,
  pageFailure,
  updateBoardRevisionState,
} from "../../../reducers/uploadedBoard";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import { useCallback, useContext, useEffect, useState } from "react";
import LoadingOverlay from "../../LoadingOverlay";
import SelectBoard from "../../SelectBoard";
import { useAppDispatch } from "../../../utils/hooks/store";
import { useCustomConfirm } from "../../../utils/hooks/alerts";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { RevisionsContainer } from "./RevisionsStyles";
import Button from "../../common/Button";
import LoadingIcon from "../../common/LoadingIcon";
import BoardCard from "../../boardViews/BoardCard";
import RevisionsOptions from "./RevisionsOptions";
import { useShipments } from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";

export default function RevisionsTool({
  revisions,
  previousRevision,
}: {
  revisions: BoardRevisions;
  previousRevision: Board | null;
}) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const { shipments: previousShipments } = useShipments(previousRevision?.id);

  const dispatch = useAppDispatch();
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(
    previousRevision,
  );
  const customConfirm = useCustomConfirm();
  const errorHandler = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);

  const { sendBoardEvent, loading: sendingEvent } =
    useSendBoardEvent(errorHandler);

  const resetPreviousRevision = useCallback(() => {
    setSelectedBoard(previousRevision);
  }, [previousRevision]);

  useEffect(() => {
    if (selectedBoard === null) {
      if (revisions.previous_revision_id !== null) {
        customConfirm(
          "Unset previous revision?",
          () => {
            void sendBoardEvent(boardId, {
              machine_group: MachineGroup.BOARD,
              model_id: boardId,
              event: BoardTransitionEvent.UPDATE_BOARD_REVISION,
              update: {
                previous_revision_board_id: null,
              },
            }).then(() => {
              dispatch(
                updateBoardRevisionState({
                  boardId,
                  boardRevisionState: BoardRevisionState.UNPROCESSED,
                }),
              );
            });
          },
          resetPreviousRevision,
        );
      }
    } else {
      if (selectedBoard.id !== revisions.previous_revision_id) {
        customConfirm(
          `Process ${selectedBoard.project_name} as a previous revision?`,
          () => {
            void sendBoardEvent(boardId, {
              machine_group: MachineGroup.BOARD,
              model_id: boardId,
              event: BoardTransitionEvent.UPDATE_BOARD_REVISION,
              update: {
                previous_revision_board_id: selectedBoard.id,
              },
            }).then(() => {
              dispatch(
                updateBoardRevisionState({
                  boardId,
                  boardRevisionState: BoardRevisionState.PROCESSING,
                }),
              );
            });
          },
          resetPreviousRevision,
        );
      }
    }
  }, [
    boardId,
    customConfirm,
    dispatch,
    resetPreviousRevision,
    revisions.previous_revision_id,
    selectedBoard,
    sendBoardEvent,
  ]);

  const processDiff = () => {
    sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.SHOULD_PROCESS_BOARD_REVISION,
    }).then(() => {
      dispatch(
        updateBoardRevisionState({
          boardId,
          boardRevisionState: BoardRevisionState.PROCESSING,
        }),
      );
    });
  };

  return (
    <RevisionsContainer>
      <LoadingOverlay loading={sendingEvent} text={"Updating revision..."} />
      <SelectBoard
        placeholder={
          "Search for a previous revision of this board to see differences..."
        }
        selectedBoard={selectedBoard}
        setSelectedBoard={setSelectedBoard}
        doNotIncludeBoardId={boardId}
      />
      {previousRevision && (
        <BoardCard board={previousRevision} shipments={previousShipments} />
      )}
      {selectedBoard !== null &&
        revisions.state == BoardRevisionState.UNPROCESSED && (
          <Button label={"Process diff"} onClick={processDiff} />
        )}
      {revisions.state == BoardRevisionState.PROCESSING && (
        <LoadingIcon text={"Processing diff..."} />
      )}
      {revisions.state == BoardRevisionState.PROCESSED && (
        <RevisionsOptions
          revisions={revisions}
          previousRevision={previousRevision}
        />
      )}
    </RevisionsContainer>
  );
}
